import { PARAM_LABELS, SOIL_GROUP_CODES, TEXTURE_LABELS } from '../constants/parameters.constants';
import type { IncompleteParam, SoilCard, SoilGroupKey, StateReferenceDTO } from '../types/parameters.types';
import { formatBand } from './format';

const dependentMeta = (criterion: 'Prem' | 'clay', min?: number, max?: number, texture?: keyof typeof TEXTURE_LABELS) => {
	if (criterion === 'Prem') return `P-rem · ${formatBand(min, max)} mg/L`;
	if (texture) return `Argila · ${TEXTURE_LABELS[texture]}`;
	return `Argila · ${formatBand(min, max)} %`;
};

const isFilled = (value: string | null | undefined) => !!value?.trim();

export const buildSoilCards = (reference: StateReferenceDTO, group: SoilGroupKey): SoilCard[] => {
	const codes = SOIL_GROUP_CODES[group];
	const cards: SoilCard[] = [];

	reference.dependentParams.forEach((param, i) => {
		if (!codes.includes(param.code)) return;
		param.rules.forEach((rule, j) => {
			rule.dependentClasses.forEach((cls, k) => {
				const classPath = ['dependentParams', i, 'rules', j, 'dependentClasses', k];
				cards.push({
					id: classPath.join('-'),
					code: param.code,
					title: PARAM_LABELS[param.code] ?? param.name,
					meta: dependentMeta(rule.criterion, cls.minValue, cls.maxValue, cls.texture),
					kind: rule.criterion,
					paramPath: ['dependentParams', i],
					classPath,
					name: param.name,
					unit: param.unit,
					method: param.method,
					idealRange: cls.idealRange,
					classes: cls.fixedClasses,
					hasBand: !cls.texture,
					isComplete: isFilled(param.name) && isFilled(param.unit) && isFilled(param.method),
					minValue: cls.minValue,
					maxValue: cls.maxValue,
					texture: cls.texture,
				});
			});
		});
	});

	reference.fixedParams.forEach((param, i) => {
		if (!codes.includes(param.code)) return;
		const paramPath = ['fixedParams', i];
		cards.push({
			id: paramPath.join('-'),
			code: param.code,
			title: PARAM_LABELS[param.code] ?? param.name,
			meta: 'Regra fixa',
			kind: 'fixed',
			paramPath,
			classPath: paramPath,
			name: param.name,
			unit: param.unit,
			method: param.method,
			idealRange: param.idealRange,
			classes: param.fixedClasses,
			hasBand: false,
			isComplete:
				isFilled(param.name) && isFilled(param.unit) && isFilled(param.method) && isFilled(param.idealRange),
		});
	});

	return cards.sort((a, b) => codes.indexOf(a.code) - codes.indexOf(b.code));
};

export const removeSoilCard = (
	reference: StateReferenceDTO,
	card: SoilCard
): Pick<StateReferenceDTO, 'dependentParams'> | Pick<StateReferenceDTO, 'fixedParams'> => {
	if (card.kind === 'fixed') {
		return { fixedParams: reference.fixedParams.filter((_, i) => i !== card.paramPath[1]) };
	}

	const [, paramIndex, , ruleIndex, , classIndex] = card.classPath as number[];
	const dependentParams = reference.dependentParams
		.map((param, i) => {
			if (i !== paramIndex) return param;
			const rules = param.rules
				.map((rule, j) =>
					j === ruleIndex
						? { ...rule, dependentClasses: rule.dependentClasses.filter((_, k) => k !== classIndex) }
						: rule
				)
				.filter((rule) => rule.dependentClasses.length > 0);
			return { ...param, rules };
		})
		.filter((param) => param.rules.length > 0);

	return { dependentParams };
};

export const listIncompleteParams = (reference: StateReferenceDTO): IncompleteParam[] => [
	...reference.fixedParams.flatMap((param, index) =>
		isFilled(param.unit) && isFilled(param.method) && isFilled(param.idealRange)
			? []
			: [
					{
						id: `fixed-${index}`,
						key: 'fixedParams' as const,
						index,
						label: PARAM_LABELS[param.code] ?? param.name,
						method: param.method ?? '',
						unit: param.unit ?? '',
						idealRange: param.idealRange ?? '',
					},
				]
	),
	...reference.dependentParams.flatMap((param, index) =>
		isFilled(param.unit) && isFilled(param.method)
			? []
			: [
					{
						id: `dependent-${index}`,
						key: 'dependentParams' as const,
						index,
						label: PARAM_LABELS[param.code] ?? param.name,
						method: param.method ?? '',
						unit: param.unit ?? '',
						idealRange: null,
					},
				]
	),
];
